/**
 * AgentCatalogInline — Compact agent catalog view for the Pipeline tab.
 * Shows all registered agents with status, capabilities, and execution stats.
 * Links to the full /agents page for management.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../apiClient';

const C = {
  text: '#1A1F2E', muted: '#57606A', light: '#8B949E',
  blue: '#0969DA', green: '#1A7F37', red: '#CF222E', orange: '#9A6700', purple: '#6639BA',
  border: '#D0D7DE', bg: '#F6F8FA', card: '#FFFFFF',
};

const CAP_COLORS = {
  'refactor': C.blue, 'generate-code': C.blue,
  'test-write': C.green, 'test-run': C.green,
  'scan': C.orange, 'security-scan': C.orange, 'analyze': C.orange, 'enrich': C.orange,
  'score': C.purple, '7rs-assess': C.purple, 'route': C.purple, 'auto-decide': C.purple,
  'generate-terraform': C.red, 'generate-cdk': C.red,
  'evaluate': '#57606A', 'gate-decision': '#57606A',
  'generate-requirements': C.blue,
};

export default function AgentCatalogInline() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAgents = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/agents`);
      if (r.ok) setAgents(await r.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  if (loading) return <div style={{ padding: 20, color: C.muted }}>Loading agents...</div>;

  const active = agents.filter(a => a.status === 'active');
  const inactive = agents.filter(a => a.status !== 'active');
  const totalExecs = agents.reduce((sum, a) => sum + (a.stats?.total_executions || 0), 0);

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Summary bar */}
      <div style={{
        display: 'flex', gap: 20, alignItems: 'center', marginBottom: 16,
        padding: '10px 16px', background: C.card, borderRadius: 8, border: `1px solid ${C.border}`,
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>🤖 Agent Catalog</span>
        <span style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>
          {active.length} active
        </span>
        {inactive.length > 0 && (
          <span style={{ fontSize: 12, color: C.light }}>{inactive.length} inactive</span>
        )}
        <span style={{ fontSize: 12, color: C.muted }}>
          {totalExecs} total executions
        </span>
        <a href="/agents" style={{
          marginLeft: 'auto', fontSize: 11, color: C.blue, textDecoration: 'none', fontWeight: 600,
        }}>
          Manage →
        </a>
      </div>

      {/* Agent grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {agents.map(agent => (
          <div key={agent.id} style={{
            padding: '12px 14px', borderRadius: 8, background: C.card,
            border: `1px solid ${agent.status === 'active' ? C.border : '#EFF2F5'}`,
            opacity: agent.status === 'active' ? 1 : 0.6,
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: agent.status === 'active' ? C.green : C.light,
              }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: C.text, flex: 1 }}>
                {agent.display_name}
              </span>
              <span style={{ fontSize: 10, color: C.light }}>v{agent.version}</span>
            </div>

            {/* Capabilities */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 8 }}>
              {(agent.capabilities || []).map(cap => (
                <span key={cap} style={{
                  fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                  background: (CAP_COLORS[cap] || C.muted) + '15',
                  color: CAP_COLORS[cap] || C.muted,
                }}>{cap}</span>
              ))}
            </div>

            {/* Stacks + Stats */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: C.muted }}>
              {(agent.stacks || []).map((s, i) => (
                <span key={i} style={{
                  padding: '1px 5px', borderRadius: 3, background: C.bg,
                  border: `1px solid ${C.border}`, fontWeight: 600, color: C.text,
                }}>
                  {s.language === '*' ? 'All' : s.language}
                </span>
              ))}
              <span style={{ marginLeft: 'auto' }}>
                {agent.stats?.total_executions || 0} runs
                {agent.stats?.success_rate > 0 && ` · ${agent.stats.success_rate}%`}
              </span>
            </div>
            {/* LLM badge */}
            <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>
              🧠 Kiro CLI (Claude Sonnet 4.6)
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}